import React, { useState, useRef, useLayoutEffect } from 'react'
import { usePhoneInput, FlagImage, defaultCountries, parseCountry } from 'react-international-phone'
import PropTypes from 'prop-types'
import { FaIcon } from '../components'

const CountryItem = ({ c, selected, onSelect }) => (
  <div
    className={`phone-dropdown-item${selected ? ' selected' : ''}`}
    onClick={() => onSelect(c)}
    >
    <FlagImage iso2={c.iso2} className='phone-dropdown-flag' />
    <span className='phone-dropdown-name'>{c.name}</span>
    <span className='phone-dropdown-iso'>{c.iso2.toUpperCase()}</span>
    <span className='phone-dropdown-dialcode'>+{c.dialCode}</span>
  </div>
)

CountryItem.propTypes = {
  c: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
}

export default function GGPhoneInput({ value, onChange, defaultCountry, label, className }) {
  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const searchBarRef = useRef(null)
  const [ dropdownOpen, setDropdownOpen ] = useState(false)
  const [ search, setSearch ] = useState('')
  const [ listMaxHeight, setListMaxHeight ] = useState(0)
  const [ dropdownPosition, setDropdownPosition ] = useState({ top: 0, left: 0, width: 0 })

  const { inputValue, country, setCountry, handlePhoneValueChange } = usePhoneInput({
    defaultCountry: defaultCountry,
    value,
    onChange: (data) => {
      if (!onChange) { return }
      // An empty field still reports the country dial code (e.g. "+40"),
      // so a prefix-only value must be surfaced as an empty phone.
      onChange(data.phone === `+${data.country.dialCode}` ? '' : data.phone)
    },
    inputRef,
  })

  // The dropdown is fixed-positioned so overflow ancestors (e.g. the popup
  // body, scrollable on mobile) cannot clip it — it must be able to extend
  // over bottom bars like .save_bar_light.
  useLayoutEffect(() => {
    if (!dropdownOpen || !containerRef.current) { return }

    const updatePosition = () => {
      if (!containerRef.current) { return }
      const { bottom, left, width } = containerRef.current.getBoundingClientRect()
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight
      const searchBarHeight = searchBarRef.current ? searchBarRef.current.offsetHeight : 52
      let maxHeight = viewportHeight - bottom - searchBarHeight - 16
      if (maxHeight > 320) {
        maxHeight = 320
      }
      setListMaxHeight(maxHeight)
      setDropdownPosition({ top: bottom + 4, left, width })
    }
    updatePosition()

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setDropdownOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    // Keep the fixed dropdown glued to the input when the popup body scrolls
    // or the viewport resizes (e.g. mobile keyboard opening).
    document.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [ dropdownOpen ])

  const handleCountrySelect = (selected) => {
    setCountry(selected.iso2, { focusOnInput: true })
    setDropdownOpen(false)
    setSearch('')
  }

  const filterList = (list, query) => {
    const q = query.toLowerCase()
    const dialQuery = q.startsWith('+') ? q.slice(1) : q
    return list
      .filter(c =>
        c.iso2.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(dialQuery),
      )
  }

  const filteredCountries = filterList(defaultCountries.map(parseCountry), search)

  return (
    <div ref={containerRef} className={className}>
      {label && <div className='text-ds-form-label add-margin-bottom-10'>{label}</div>}
      <div className='react-international-phone-input-container'>
        <div className='react-international-phone-country-selector'>
          <button
            type='button'
            className='react-international-phone-country-selector-button'
            onClick={() => setDropdownOpen(prev => !prev)}
          >
            <div className='react-international-phone-country-selector-button__button-content'>
              <FlagImage
                iso2={country.iso2}
                className='react-international-phone-country-selector-button__flag-emoji'
              />
              <span className='react-international-phone-country-selector-button__country-iso'>
                {country.iso2.toUpperCase()}
              </span>
              <div className={`react-international-phone-country-selector-button__dropdown-arrow${dropdownOpen ? ' react-international-phone-country-selector-button__dropdown-arrow--active' : ''}`} />
            </div>
          </button>
        </div>
        <input
          ref={inputRef}
          className='react-international-phone-input'
          value={inputValue}
          onChange={handlePhoneValueChange}
        />
        {dropdownOpen && (
          <div
            className='phone-custom-dropdown'
            style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width }}
          >
            <div className='phone-dropdown-search' ref={searchBarRef}>
              <FaIcon icon='magnifying-glass'/>
              <input
                type='text'
                placeholder={window.I18n.t('shared.components.gg_phone_input.search_country')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className='phone-dropdown-list' style={{ maxHeight: listMaxHeight }}>
              {filteredCountries.map(c => (
                <CountryItem key={c.iso2} c={c} selected={c.iso2 === country.iso2} onSelect={handleCountrySelect} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

GGPhoneInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  defaultCountry: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
}
